import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ArtePersonalizadaService,
  CampoArte,
  ContextoArte,
  ModeloArte
} from '../../../shared/services/arte-personalizada.service';

export interface ArtePersonalizarData {
  /** A arte (PNG/JPG) anexada ao envio. */
  arquivo: File;
  /** Nomes reais dos destinatários, usados para pré-visualizar o resultado. */
  nomes: string[];
  /** Modelo já calibrado numa campanha anterior, se houver. */
  modelo?: ModeloArte;
}

/**
 * Marca na arte onde cada placeholder deve ser substituído.
 *
 * O operador arrasta um retângulo em volta do "{NOME}" desenhado na imagem; o
 * sistema amostra sozinho a cor do fundo e a do texto, cobre o placeholder e
 * escreve o valor real. Só funciona bem sobre fundo chapado — que é o caso das
 * artes de campanha exportadas do Canva.
 */
@Component({
  selector: 'app-arte-personalizar-dialog',
  templateUrl: './arte-personalizar-dialog.component.html',
  styleUrls: ['./arte-personalizar-dialog.component.css']
})
export class ArtePersonalizarDialogComponent implements OnInit {

  @ViewChild('tela', { static: false }) tela: ElementRef<HTMLDivElement>;
  @ViewChild('preview', { static: false }) preview: ElementRef<HTMLCanvasElement>;

  campos: CampoArte[] = [];
  desconto = 20;
  prazoDiasUteis = 15;

  imagem: HTMLImageElement;
  carregando = true;
  erro: string;

  /** Índice do campo que está sendo marcado; -1 quando ninguém está em edição. */
  marcando = -1;
  /** Retângulo em construção, em % da imagem. */
  rascunho: { x: number; y: number; w: number; h: number };

  /** Qual nome está sendo pré-visualizado. */
  indicePreview = 0;

  private arrastando = false;
  private inicio: { x: number; y: number };

  constructor(
    private arteService: ArtePersonalizadaService,
    public dialogRef: MatDialogRef<ArtePersonalizarDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ArtePersonalizarData
  ) { }

  ngOnInit(): void {
    const modelo = this.data?.modelo;
    this.campos = modelo && modelo.campos && modelo.campos.length
      ? JSON.parse(JSON.stringify(modelo.campos))
      : this.arteService.camposPadrao();
    if (modelo) {
      this.desconto = modelo.desconto || this.desconto;
      this.prazoDiasUteis = modelo.prazoDiasUteis || this.prazoDiasUteis;
    }

    this.arteService.carregarImagem(this.data.arquivo).then(img => {
      this.imagem = img;
      this.carregando = false;
      setTimeout(() => this.atualizarPreview(), 0);
    }).catch(e => {
      this.carregando = false;
      this.erro = e?.message || 'Não foi possível abrir a imagem.';
    });
  }

  get nomes(): string[] {
    return (this.data?.nomes || []).filter(n => !!n);
  }

  get nomePreview(): string {
    const lista = this.nomes;
    return lista.length ? lista[Math.min(this.indicePreview, lista.length - 1)] : 'Fulano de Tal';
  }

  get camposMarcados(): number {
    return this.campos.filter(c => !!c.caixa).length;
  }

  get pronto(): boolean {
    return this.camposMarcados > 0;
  }

  contexto(nome: string): ContextoArte {
    return {
      nome,
      desconto: this.desconto,
      dataBase: new Date(),
      prazoDiasUteis: this.prazoDiasUteis
    };
  }

  /** Texto já resolvido, mostrado ao lado de cada campo na lista. */
  resultado(campo: CampoArte): string {
    return this.arteService.resolver(campo.template, this.contexto(this.nomePreview));
  }

  // ------------------------------------------------------------- marcação

  iniciarMarcacao(indice: number): void {
    this.marcando = this.marcando === indice ? -1 : indice;
    this.rascunho = undefined;
  }

  limparCampo(indice: number): void {
    this.campos[indice].caixa = undefined;
    this.atualizarPreview();
  }

  onMouseDown(ev: MouseEvent): void {
    if (this.marcando < 0) {
      return;
    }
    ev.preventDefault();
    this.arrastando = true;
    this.inicio = this.posicaoRelativa(ev);
    this.rascunho = { x: this.inicio.x, y: this.inicio.y, w: 0, h: 0 };
  }

  onMouseMove(ev: MouseEvent): void {
    if (!this.arrastando || this.marcando < 0) {
      return;
    }
    const atual = this.posicaoRelativa(ev);
    this.rascunho = {
      x: Math.min(this.inicio.x, atual.x),
      y: Math.min(this.inicio.y, atual.y),
      w: Math.abs(atual.x - this.inicio.x),
      h: Math.abs(atual.y - this.inicio.y)
    };
  }

  onMouseUp(): void {
    if (!this.arrastando || this.marcando < 0) {
      return;
    }
    this.arrastando = false;

    // Arrasto minúsculo costuma ser clique acidental — descarta.
    if (!this.rascunho || this.rascunho.w < 0.01 || this.rascunho.h < 0.01) {
      this.rascunho = undefined;
      return;
    }

    const campo = this.campos[this.marcando];
    campo.caixa = { ...this.rascunho };

    // As cores saem da própria arte: o operador não precisa acertar o hex.
    try {
      const cores = this.arteService.amostrarCores(this.imagem, campo.caixa);
      campo.corFundo = cores.corFundo;
      campo.corTexto = cores.corTexto;
    } catch (e) {
      console.warn('Não foi possível amostrar as cores da arte', e);
    }

    this.rascunho = undefined;
    this.marcando = -1;
    this.atualizarPreview();
  }

  private posicaoRelativa(ev: MouseEvent): { x: number; y: number } {
    const rect = this.tela.nativeElement.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (ev.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (ev.clientY - rect.top) / rect.height))
    };
  }

  // -------------------------------------------------------------- preview

  atualizarPreview(): void {
    if (!this.imagem || !this.preview) {
      return;
    }
    const gerado = this.arteService.renderizar(this.imagem, this.campos, this.contexto(this.nomePreview));
    const alvo = this.preview.nativeElement;
    alvo.width = gerado.width;
    alvo.height = gerado.height;
    const g = alvo.getContext('2d');
    if (g) {
      g.drawImage(gerado, 0, 0);
    }
  }

  proximoNome(): void {
    const total = this.nomes.length;
    if (total > 1) {
      this.indicePreview = (this.indicePreview + 1) % total;
      this.atualizarPreview();
    }
  }

  aplicar(): void {
    const modelo: ModeloArte = {
      campos: this.campos.filter(c => !!c.caixa),
      desconto: this.desconto,
      prazoDiasUteis: this.prazoDiasUteis
    };
    this.dialogRef.close(modelo);
  }

  cancelar(): void {
    this.dialogRef.close(null);
  }
}
